// The Order Chat "Refill sheet" screen's rules (group-home roster refills, PR-E; O154 F5
// F7, O169, O170), kept OUT of the components so a test drives them directly with the
// shapes GET /api/sms/chat/roster/:id actually returns (routes/orderChatRoster.js +
// services/orderChatRoster/summary.js).
//
// What lives here: the file input's accept list, the progress step a job is on, when
// polling stops and how it backs off, which model read the sheet, every enum the server
// sends in plain words, the per-line facts the summary shows, the printable rows (F7),
// how the sheet's needs-attention cards stay ON the sheet, and the scope (b) search's
// small state machine. CommonJS, like lib/order-batch.js — Jest reads it directly and
// esbuild interops it for the bundle.

const { markBatchResults } = require('./order-batch.js');

// ── Upload ────────────────────────────────────────────────────────────────────

// The file input's accept list. NOT image/heic (O169): with it absent, iOS Safari hands
// a library photo over as a JPEG. A HEIC that arrives by drag-drop or paste anyway is
// handled by lib/image-resize.
const ROSTER_ACCEPT = 'application/pdf,image/jpeg,image/png';

// A selection the server would take: one PDF, or 1–4 page images (uploadLimits: "Send page
// images or a PDF, not both"; ROSTER_MAX_PAGES). `kinds`: 'pdf' | 'image' per file.
// → null when fine, else the sentence to show.
// `total`: { bytes, max } — every page image of one upload together is held to the
// server's document cap (uploadLimits: 15 MB), as the server holds it.
function selectionProblem(kinds, maxPages = 4, total = null) {
  const list = Array.isArray(kinds) ? kinds : [];
  if (!list.length) return 'Add a photo or a PDF of the sheet.';
  const pdfs = list.filter((k) => k === 'pdf').length;
  const images = list.filter((k) => k === 'image').length;
  if (pdfs + images !== list.length) return "One of these files isn't a photo or a PDF.";
  if (pdfs && images) return 'Send photos of the pages or one PDF, not both.';
  if (pdfs > 1) return 'Send one PDF at a time.';
  if (images > maxPages) return `A sheet is at most ${maxPages} pages. Remove ${images - maxPages}.`;
  if (images && total && total.max > 0 && total.bytes > total.max) {
    return `These pages add up to more than ${Math.round(total.max / (1024 * 1024))} MB. Send fewer pages, or scan the sheet to one PDF.`;
  }
  return null;
}

// Whether the dialog may upload, and why not — pure, so a test drives what a click
// can't (#749 r1 MINOR-5): a home picked, at least one page ready, nothing still being
// prepared, NO page that failed to prepare (a failed page is never dropped silently),
// the selection one the server takes, every page counted toward the total, and no upload
// already under way.
//   files: [{ status: 'preparing'|'ready'|'error', kind, prepared: { bytes } }]
//   limits: normalized (lib/image-resize normalizeLimits) — maxPages, docMaxBytes
function uploadReadiness({ facilityId, files, limits, uploading = false }) {
  const list = Array.isArray(files) ? files : [];
  const ready = list.filter((f) => f && f.status === 'ready');
  const preparing = list.some((f) => f && f.status === 'preparing');
  const failed = list.some((f) => f && f.status === 'error');
  const totalBytes = ready.filter((f) => f.kind === 'image')
    .reduce((n, f) => n + ((f.prepared && f.prepared.bytes && f.prepared.bytes.length) || 0), 0);
  const l = limits || {};
  const problem = list.length && !preparing
    ? selectionProblem(ready.map((f) => f.kind), l.maxPages || 4, { bytes: totalBytes, max: l.docMaxBytes || 0 })
    : null;
  const canUpload = !!facilityId && ready.length > 0 && !preparing && !failed && !problem && !uploading;
  return { canUpload, problem, totalBytes, preparing, failed, ready };
}

// The upload itself, through an injected client — pure, so a test drives the answers a
// click would get (#749 r1 N8): a 202 must name its sheet; one that doesn't is an error the
// dialog shows (and it leaves "Uploading…"), never a silent stop.
const UPLOAD_NO_ID = "The sheet was sent, but no sheet came back. Look for it under Your recent sheets, or upload it again.";
async function submitUpload(client, body) {
  try {
    const r = await client.uploadRosterSheet(body);
    const rosterId = r && typeof r.rosterId === 'string' && r.rosterId ? r.rosterId : null;
    return rosterId ? { ok: true, rosterId } : { ok: false, error: UPLOAD_NO_ID };
  } catch (e) {
    return { ok: false, error: (e && e.message) || ERROR_TEXT.internal };
  }
}

// The dialog's Upload, end to end: a refusal or an answer without a sheet is shown (and
// "Uploading…" left); a sheet is handed on. Pure over its callbacks, so a test drives it.
async function performUpload({ client, body, onError, onDone }) {
  const r = await submitUpload(client, body);
  if (!r.ok) {
    if (onError) onError(r.error);
    return r;
  }
  if (onDone) onDone(r.rosterId);
  return r;
}

// "N charts in scope" (078's facility editor's words).
function chartsInScopeText(n) {
  const k = Number(n) || 0;
  return `${k} chart${k === 1 ? '' : 's'} in scope`;
}

// ── Progress + polling ────────────────────────────────────────────────────────

const PROGRESS_STEPS = Object.freeze([
  { key: 'queued', label: 'Queued' },
  { key: 'reading', label: 'Reading the sheet' },
  { key: 'staging', label: 'Matching residents and drafting' },
  { key: 'done', label: 'Done' },
]);

// queued → reading → staging → done, or failed. A job is 'done' when the READ finished;
// its lines are only final once staging has run (PR-D), so until then it is 'staging'.
function progressStepOf(job) {
  if (!job) return 'queued';
  if (job.status === 'failed') return 'failed';
  if (job.status === 'queued') return 'queued';
  if (job.status === 'running') return 'reading';
  if (job.status === 'done') {
    const st = job.staging && job.staging.status;
    if (st === 'staged' || st === 'failed') return 'done';
    return 'staging';
  }
  return 'queued';
}

// The waits between polls: quick while a read usually finishes (~30–75 s), then easing
// off to 10 s. The poll stops on its own after POLL_MAX_MS (C11's stale bound is 15
// minutes; a sheet still not staged by then says so and offers a manual refresh).
const POLL_DELAYS_MS = Object.freeze([1500, 2000, 2000, 3000, 3000, 5000, 5000, 8000]);
const POLL_CAP_MS = 10000;
const POLL_MAX_MS = 10 * 60 * 1000;
const POLL_MAX_ERRORS = 5;

function nextPollDelay(attempt) {
  const n = Math.max(0, Number(attempt) || 0);
  return n < POLL_DELAYS_MS.length ? POLL_DELAYS_MS[n] : POLL_CAP_MS;
}

// Keep polling? Stops on: failed; read AND staged (or staging failed); the sheet
// discarded; a 404 (gone: purged, or the flag turned off); too many errors in a row; the
// overall time bound. → { poll: boolean, why }
function pollDecision(job, { elapsedMs = 0, errors = 0, notFound = false } = {}) {
  if (notFound) return { poll: false, why: 'gone' };
  if (errors >= POLL_MAX_ERRORS) return { poll: false, why: 'errors' };
  if (job && job.status === 'failed') return { poll: false, why: 'failed' };
  if (job && job.staging && job.staging.discardedAt) return { poll: false, why: 'discarded' };
  if (job && job.status === 'done' && job.staging
      && (job.staging.status === 'staged' || job.staging.status === 'failed')) {
    return { poll: false, why: 'final' };
  }
  if (elapsedMs >= POLL_MAX_MS) return { poll: false, why: 'timeout' };
  return { poll: true, why: null };
}

// One sheet's poller. Injected clock and timer, so a test walks it step by step.
//   fetchJob() → Promise<job>     (rejects with err.status 404 when the sheet is gone)
//   onJob(job), onStop(why, job)
function createSheetPoller({ fetchJob, onJob, onStop, setTimer = setTimeout, clearTimer = clearTimeout, now = Date.now }) {
  let timer = null;
  let attempt = 0;
  let errors = 0;
  let started = 0;
  let stopped = false;
  let last = null;
  let inFlight = false;

  const finish = (why) => {
    stopped = true;
    if (timer != null) clearTimer(timer);
    timer = null;
    if (onStop) onStop(why, last);
  };
  async function tick() {
    timer = null;
    if (stopped || inFlight) return;
    inFlight = true;
    let notFound = false;
    try {
      last = await fetchJob();
      errors = 0;
      if (onJob) onJob(last);
    } catch (err) {
      if (err && err.status === 404) notFound = true;
      else errors += 1;
    } finally {
      inFlight = false;
    }
    if (stopped) return;
    const d = pollDecision(last, { elapsedMs: now() - started, errors, notFound });
    if (!d.poll) { finish(d.why); return; }
    timer = setTimer(tick, nextPollDelay(attempt));
    attempt += 1;
  }
  return {
    start() { started = now(); stopped = false; attempt = 0; errors = 0; tick(); },
    stop() { if (!stopped) finish('stopped'); },
    isRunning() { return !stopped; },
  };
}

// ── Which model read it ───────────────────────────────────────────────────────

// The aliases modelResolver.js admits for orderChatRoster (settings.jsx INFERENCE_CHOICES
// names them the same way).
const MODEL_NAMES = Object.freeze({
  'bedrock-sonnet': 'Sonnet',
  'bedrock-opus-4-6': 'Opus',
  'bedrock-haiku': 'Haiku',
  'openai-sol': 'Sol',
  'openai-luna': 'Luna',
});
function modelName(alias) {
  if (!alias) return null;
  return MODEL_NAMES[alias] || String(alias);
}

// "Read by Sonnet", or — when the cross-vendor retry produced the lines (O167(a)) —
// "Read by the fallback (Sol)" and why.
function readByText(job) {
  if (!job || !job.modelUsed) return null;
  const used = modelName(job.modelUsed);
  if (!job.fallbackUsed) return `Read by ${used}`;
  const primary = modelName(job.modelRequested);
  const why = String(job.primaryOutcome || '').startsWith('invalid')
    ? "didn't give a usable read"
    : "didn't answer";
  return primary && primary !== used
    ? `Read by the fallback (${used}) — ${primary} ${why}`
    : `Read by the fallback (${used})`;
}

// ── Plain words for every enum the server sends ──────────────────────────────

// roster_jobs.error_code (store.ERROR_CODES) — what happened and what to do next.
const ERROR_TEXT = Object.freeze({
  not_a_roster: "This doesn't look like a refill sheet. Check the photo is the facility's sheet and upload it again.",
  too_many_lines: 'This sheet has more than 40 lines. Upload it in two parts.',
  invalid_output: "The sheet couldn't be read reliably. Try a sharper, straight-on photo or a scan.",
  model_error: "The reading service didn't answer. Try again in a minute.",
  pages_unreadable: "The pages couldn't be opened. Try a JPEG or PNG photo, or a PDF.",
  storage: "The sheet couldn't be stored. Try again.",
  stale: 'Reading took too long and was stopped. Upload the sheet again.',
  internal: 'Something went wrong reading the sheet. Try again.',
});
function errorText(code) {
  return ERROR_TEXT[code] || ERROR_TEXT.internal;
}

// Why a line needs the provider (store.NEEDS_ATTENTION_REASONS, #747 r2). Several come
// from the owner's switches (orderChatRoster/policy.js): a clear sheet strength the chart
// does not hold is asked about (OWNER-1), an unsure name or drug is asked about unless the
// match or the reader's guess settles it (OWNER-2 / 2b), and two release families always
// ask (D-5). A duplicate drafted anyway is a card under its duplicate reason.
const NEEDS_ATTENTION_TEXT = Object.freeze({
  resident_ambiguous: 'More than one resident could be this name — pick the right one on the card.',
  resident_not_found: 'No resident of this home has this name. Search all patients to find the chart.',
  resident_uncertain: "The resident's name was hard to read — confirm the resident on the card.",
  med_ambiguous_strength: 'The chart has more than one strength of this drug — pick one on the card.',
  med_several_threads: 'The chart lists this drug more than once — pick the right one on the card.',
  med_release_ambiguous: 'The chart has both a regular and an extended-release form — pick one on the card.',
  med_old: "The chart's entry for this drug is over a year old — confirm it on the card.",
  med_guess_disagrees: "The drug as written and the reader's guess point to different chart meds — pick the right one on the card.",
  med_by_guess: "Only the reader's guess matched a chart med, not the drug as written — check it on the card.",
  med_combination: "The chart's med is a combination product the sheet doesn't name — check it on the card.",
  med_strength_differs: "The sheet's strength isn't the one on the chart — confirm the strength on the card.",
  drug_illegible: 'The drug name was hard to read — check the card against the sheet.',
  drug_uncertain: "The drug name was hard to read and the reader's guess didn't confirm it — check the card against the sheet.",
  auto_draft_off: 'Auto-drafting is off for this practice — review and confirm the card.',
  auto_draft_rejected: "The automatic draft couldn't be made in Elation — review and confirm the card.",
  no_primary_physician: 'The chart has no primary physician to draft under — review and confirm the card.',
  prescriber_inactive: "The chart's primary physician isn't active in Elation — review and confirm the card.",
  duplicate_line: 'This refill is on the sheet more than once. Its card is here because you chose to draft it anyway.',
  duplicate_recent: 'This refill was drafted from a sheet in the last 7 days. Its card is here because you chose to draft it anyway.',
  duplicate_order_chat: 'The same refill was filed in Order Chat for this resident in the last 7 days. Its card is here because you chose to draft it anyway.',
});
// Why a line was not drafted (store.NOT_DRAFTED_REASONS + summary's derived reasons).
const NOT_DRAFTED_TEXT = Object.freeze({
  name_illegible: "The resident's name couldn't be read.",
  not_on_chart: "This drug isn't on the resident's chart. Start a new prescription if it's needed.",
  chart_unavailable: "The resident's chart couldn't be reached in Elation.",
  draft_rejected: 'Elation refused the draft.',
  draft_unconfirmed: "Elation didn't confirm the draft. Check the chart before ordering it again.",
  stage_failed: "This line couldn't be set up.",
  no_reviewer: 'There was no one to review this line.',
  duplicate_line: 'This refill is already on this sheet, so it was drafted once.',
  duplicate_recent: 'This refill was already drafted from a sheet in the last 7 days, so it was not drafted again.',
  duplicate_order_chat: 'The same refill was filed in Order Chat for this resident in the last 7 days, so it was not drafted again.',
  cancelled: 'The card was cancelled.',
  discarded: 'The sheet was discarded.',
});
function reasonText(status, reason) {
  if (status === 'needs_attention') return NEEDS_ATTENTION_TEXT[reason] || 'Needs your review.';
  if (status === 'not_drafted') return NOT_DRAFTED_TEXT[reason] || 'Not drafted.';
  return null;
}

// Where a duplicate's first copy is (outcome.duplicateOf { rosterId, line }).
function duplicateOfText(outcome, rosterId) {
  const d = outcome && outcome.duplicateOf;
  if (!d || d.line == null) return null;
  return String(d.rosterId) === String(rosterId) ? `Same as line ${d.line} of this sheet.` : `Same as line ${d.line} of an earlier sheet.`;
}

// What the provider can do from the summary itself (outcome.actions, #747 r2 D4): the
// scope (b) search, a retry of a line the chart read or the staging could not finish, and
// "draft anyway" for a duplicate (a card to confirm — never an automatic draft).
const ACTION_LABELS = Object.freeze({
  retry: 'Try again',
  draft_anyway: 'Draft anyway',
});

// D15: Elation would not attach the draft to the chart's prescription, so it went in as a
// NEW prescription (the Order Chat precedent says the same).
const THREAD_FALLBACK_TEXT = "Drafted as a new prescription — Elation wouldn't attach it to the chart's existing one. Verify the chart.";

const STATUS_LABELS = Object.freeze({
  drafted: 'Drafted',
  needs_attention: 'Needs review',
  not_drafted: 'Not drafted',
  pending: 'Working on it',
});
function statusLabel(status) {
  return STATUS_LABELS[status] || STATUS_LABELS.pending;
}

// Per-field legibility as the reader judged it (clear | uncertain | illegible).
const LEGIBILITY_LABELS = Object.freeze({ clear: 'clear', uncertain: 'unsure', illegible: "couldn't read" });
function legibilityLabel(v) {
  return LEGIBILITY_LABELS[v] || null;
}

// ── One line's facts ──────────────────────────────────────────────────────────

// 1950-03-07 → 03/07/1950 (the card header's DOB form). Anything else is shown as given.
function formatDob(dob) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(dob || ''));
  return m ? `${m[2]}/${m[3]}/${m[1]}` : (dob ? String(dob) : '');
}

function residentLabel(patient) {
  if (!patient) return null;
  const name = `${patient.firstName || ''} ${patient.lastName || ''}`.trim();
  if (!name) return null;
  return patient.dob ? `${name} · DOB ${formatDob(patient.dob)}` : name;
}

// The sheet's own words for the drug.
function sheetMedText(line) {
  if (!line) return '';
  return [line.drugAsWritten, line.strengthAsWritten].filter(Boolean).join(' ').trim();
}

// The sheet's strength and the chart's for a line (#747 r2 D7: `sheetStrength`,
// `chartStrength`; the sheet's own words when the outcome has none).
function strengthsOf(outcome, line) {
  const sheet = (outcome && outcome.sheetStrength) || (line && line.strengthAsWritten) || null;
  const chart = (outcome && outcome.chartStrength) || null;
  return { sheet, chart };
}

// The strength as the provider reads it. A closest-on-chart line ALWAYS names both sides
// (OWNER-1: the Elation draft has no private note, so this summary — and its printout —
// is the only place the difference is written down); with no chart strength recorded,
// the chart side names the chart med itself (`chartMedName`, N1). Any other line whose two
// strengths differ says so too.
//   facts: { strengthRule, sheetStrength, chartStrength, chartMedName } — a drafted line
//          passes what was FILED (draftedStrengthFacts), any other line what staging matched.
function strengthRuleText(facts, line) {
  if (!facts) return null;
  const { sheet, chart } = strengthsOf(facts, line);
  const rule = facts.strengthRule;
  if (rule === 'closest_on_chart') {
    return `Closest on chart: sheet ${sheet || '(no strength written)'} → chart ${chart || facts.chartMedName || '(see the chart)'}`;
  }
  if (rule === 'exact') return `Same strength as the sheet (${chart || sheet || 'as written'})`;
  if (rule === 'picked') return chart ? `Strength picked from the chart: ${chart} (sheet ${sheet || 'none written'})` : 'Strength picked from the chart';
  if (sheet && chart && sheet.replace(/\s+/g, '').toLowerCase() !== chart.replace(/\s+/g, '').toLowerCase()) {
    return `Sheet ${sheet} · chart ${chart}`;
  }
  return null;
}

// The chart med as matched (#747 r2 `chartMedName`), else the sheet's words — for a line
// that was NOT drafted only. A drafted line says what was filed (draftedFacts), never the
// sheet's words.
function medAsMatched(outcome, line) {
  return (outcome && outcome.chartMedName) || sheetMedText(line);
}

// What a DRAFTED line filed — #747 r2c's `outcome.filed`, the one record of it: the row as
// auto-drafted, or the card's own session as confirmed (its picked patient, product,
// thread and draft id). A drafted line is reported from this and from nothing else: never
// staging's guess, never the sheet's words or name (#749 adjudication r1, D1). Missing
// fields stay null, and the screen and the paper say "see the chart" / "check Elation".
function draftedFacts(outcome) {
  const f = outcome && outcome.filed && typeof outcome.filed === 'object' ? outcome.filed : null;
  return {
    via: f && (f.via === 'card' || f.via === 'auto') ? f.via : null,
    draftId: f && f.draftId != null ? String(f.draftId) : null,
    patient: (f && f.patient) || null,
    medName: (f && f.medName) || null,
    strengthRule: (f && f.strengthRule) || null,
    chartStrength: (f && f.chartStrength) || null,
    rxToPharmacy: !!(f && f.rxToPharmacy),
    threadFallback: !!(f && f.threadFallback),
  };
}
// …and its strength facts for strengthRuleText: the filed rule and chart strength, the
// filed med as the chart side's name, and the sheet's own strength.
function draftedStrengthFacts(outcome, line) {
  const f = draftedFacts(outcome);
  return {
    strengthRule: f.strengthRule,
    chartStrength: f.chartStrength,
    chartMedName: f.medName,
    sheetStrength: (outcome && outcome.sheetStrength) || (line && line.strengthAsWritten) || null,
  };
}
const DRAFTED_UNKNOWN = Object.freeze({
  screenResident: '— not readable here; check Elation',
  screenMedCard: 'from your card — see the chart in Elation',
  screenMed: '— see the chart in Elation',
  printResident: '(check Elation)',
  printMed: '(see the chart)',
});

// Elation's user-facing chart (components/fax-filed-item.jsx, lib/intake-api.js). Elation
// has no link to one draft; the draft is on the chart's medication list, unsigned.
function elationChartUrl(elationPatientId) {
  if (!elationPatientId) return null;
  return `https://app.elationemr.com/patient/${encodeURIComponent(elationPatientId)}/`;
}

// The page viewer's image (an <img src>: cookie auth, a GET).
function sheetPageUrl(rosterId, page) {
  return `/api/sms/chat/roster/${encodeURIComponent(rosterId)}/pages/${encodeURIComponent(page)}`;
}

// ── The line's own handwriting (O180, shape O181) ────────────────────────────
//
// The read also says where each row sits on its page: `rowPage` (0-based) and `rowBand`
// {y0, y1} — the row's top and bottom as INTEGER per-mille of the page height (top = 0,
// bottom = 1000), relative to the page image the model was sent, which IS the image the
// page route serves (services/orderChatRoster/pages.js sends an upload as itself only
// when it is upright, and storage.putPages keeps exactly those bytes, or the derived
// page, under the job's page key). The summary shows that row as a band cut from the
// page: the full page width (every column), padded by CROP_PAD of the row's height above
// and below, drawn at least CROP_MIN_BAND of the page tall, clamped to the page, never
// cutting the row itself. A line whose band is missing, does not hold together, or is
// implausible for one handwritten row — under CROP_MIN_ROW or over CROP_MAX_ROW per-mille
// tall — shows what it always did: the "View on the sheet" link to the whole page.
const ROW_BAND_SCALE = 1000;          // per-mille (O181)
const CROP_MIN_ROW = 5;               // ‰: ~11 px of a 2,200 px page — no handwritten row is thinner (a 40-row sheet's rows are ~15‰)
const CROP_MAX_ROW = 200;             // ‰: a fifth of the page for ONE row is not a row
const CROP_PAD = 0.35;
const CROP_MIN_BAND = 0.03;           // the drawn band's least height, as a fraction of the page (rendering, not data)
const CROP_MAX_BAND = 0.2;            // the drawn band's greatest height, unless the row itself is taller
// Until the page image has loaded, the band's height is drawn for a letter page (the
// page gate's 1,700 × 2,200); the image's own size replaces it on load.
const CROP_DEFAULT_PAGE = Object.freeze({ width: 1700, height: 2200 });

const perMille = (v) => Number.isInteger(v) && v >= 0 && v <= ROW_BAND_SCALE;

// The line's band as the server sent it, or null: a 0-based page inside the sheet's page
// count, two per-mille integers with y0 < y1, and a plausible height for one row. Never
// adjusted — a band that is off is not shown at all.
function lineRowBand(line, pageCount = null) {
  const b = line && line.rowBand;
  const page = line && line.rowPage;
  if (!b || typeof b !== 'object' || !Number.isInteger(page) || page < 0) return null;
  const max = Number.isInteger(pageCount) && pageCount > 0 ? pageCount : null;
  if (max != null && page >= max) return null;
  const { y0, y1 } = b;
  if (!perMille(y0) || !perMille(y1) || !(y0 < y1)) return null;
  const h = y1 - y0;
  if (h < CROP_MIN_ROW || h > CROP_MAX_ROW) return null;
  return { page, y0, y1 };
}

// The band a crop shows, as fractions of the page's height: { top, bottom }, or null.
// `band` is the row's per-mille {y0, y1}.
function cropBand(band, { pad = CROP_PAD, minBand = CROP_MIN_BAND, maxBand = CROP_MAX_BAND } = {}) {
  if (!band || !perMille(band.y0) || !perMille(band.y1) || !(band.y0 < band.y1)) return null;
  const y0 = band.y0 / ROW_BAND_SCALE;
  const y1 = band.y1 / ROW_BAND_SCALE;
  const h = y1 - y0;
  const centre = (y0 + y1) / 2;
  // padded, at least the minimum, at most the ceiling — but never shorter than the row
  let span = Math.max(minBand, Math.min(h * (1 + 2 * pad), Math.max(maxBand, h)));
  span = Math.min(1, span);
  let top = centre - span / 2;
  let bottom = centre + span / 2;
  // Clamped to the page, keeping the span: a row at the very top or bottom of the page
  // shows the page's own edge rather than a shorter band.
  if (top < 0) { bottom = Math.min(1, bottom - top); top = 0; }
  if (bottom > 1) { top = Math.max(0, top - (bottom - 1)); bottom = 1; }
  return { top, bottom };
}

// How to draw a band at the card's width, from the page image's natural size: the
// frame's aspect ratio (page width : the band's height in pixels) and how far up the
// image sits inside it, as a percentage of the FRAME's height (CSS `top`). With the row's
// own band, also where the row starts and ends inside the frame (`rowTopPct`,
// `rowBottomPct`): the padding above and below it is dimmed, so the row the line is
// about stands out from the neighbours the padding (or a tilted page) brings in.
function cropLayout(view, natural = null, band = null) {
  if (!view) return null;
  const n = natural && natural.width > 0 && natural.height > 0 ? natural : CROP_DEFAULT_PAGE;
  const span = view.bottom - view.top;
  if (!(span > 0)) return null;
  const at = (y) => Number((Math.min(1, Math.max(0, (y - view.top) / span)) * 100).toFixed(4));
  return {
    aspectRatio: `${n.width} / ${Math.max(1, Math.round(n.height * span))}`,
    imgTopPct: Number((-(view.top / span) * 100).toFixed(4)),
    ...(band && perMille(band.y0) && perMille(band.y1) && band.y0 < band.y1
      ? { rowTopPct: at(band.y0 / ROW_BAND_SCALE), rowBottomPct: at(band.y1 / ROW_BAND_SCALE) } : {}),
  };
}

// Everything the screen needs for one line's crop, or null (→ the link only):
//   page    the page route's 1-based page number (the band's page is 0-based)
//   band    the row's own per-mille band (the viewer highlights it, full width)
//   view    the padded band the crop shows, as fractions of the page
function lineCrop(line, pageCount = null) {
  const band = lineRowBand(line, pageCount);
  const view = band && cropBand(band);
  if (!band || !view) return null;
  return { page: band.page + 1, band, view };
}

// The viewer's scroll position that centres the highlighted band in its scroll container
// (the modal), clamped to what the container can scroll. Every value is in the
// container's own pixels: `targetTop` is the band's offset from the container's content
// top. → the new scrollTop.
function centredScrollTop({ targetTop, targetHeight, viewportHeight, scrollHeight }) {
  const want = targetTop - Math.max(0, (viewportHeight - targetHeight) / 2);
  const max = Math.max(0, (scrollHeight || 0) - (viewportHeight || 0));
  return Math.max(0, Math.min(max, Math.round(want)));
}

// ── A sheet card answers IN PLACE (O180) ─────────────────────────────────────
//
// The sheet's cards are answered through the same keyed doors the chat uses (R-CF-7:
// /pick names the session, the question, the option and the version; /order-send and
// /cancel name their session and version), but the answer lands ON the line: no
// transcript entry, no scroll, no focus jump. The server's own sentence about it is
// spoken in the sheet's live region (R-RX-10: the server stays the one author, only where
// its words land moves), the reply reconciles the sheet's batch, and the sheet re-reads
// itself so the line shows its new outcome (`outcome.filed` once filed).
//
// One answer, end to end — pure over its callbacks so a test drives it:
//   run()            the keyed call → the reply (throws on a refusal, err.body the refreshed card)
//   onReply(reply)   the page folds the reply into the sheet (never the transcript)
//   onRefused(body)  a refusal's refreshed card (the views only)
//   mark(patch)      the sheet turn's per-card state: { pending, error, announce, focus }
//   refresh()        re-read the sheet
//   said(reply)      what to announce (default: the server's own reply lines)
//   inputAt          the page's input count when the answer STARTED (I-FOCUS, below)
// → { ok: true, reply } | { ok: false, error }
const SHEET_SEND_UNCONFIRMED = "The draft was created, but Elation didn't confirm it was sent — open the chart in Elation to check it.";

// Why an answer was refused, in words (#763 adjudication r1 D1). A refusal always says
// why, and never claims an outcome the page doesn't know (a dropped connection can follow
// a filed draft):
//   1. the server's own sentence (`error`);
//   2. else its list of reasons (`errors[]` — /order-send's gate and Elation's own refusal);
//   3. else `error.message` (a route that answers {error: {message}});
//   4. else, when the server answered with no words, a fixed sentence naming the status;
//   5. else (no answer at all: a network failure, a timeout) a fixed sentence saying so.
// The transport's own token ("HTTP 409", "Failed to fetch") is never what the line says.
const REFUSAL_NO_ANSWER = "The server didn't answer. Check this line before trying again.";
function refusalNoReason(status) {
  return `The server answered with an error (${status}) and gave no reason. Check this line before trying again.`;
}
function refusalText(e) {
  const body = e && e.body && typeof e.body === 'object' ? e.body : null;
  if (body && typeof body.error === 'string' && body.error.trim()) return body.error.trim();
  if (body && Array.isArray(body.errors)) {
    const words = body.errors.filter((x) => typeof x === 'string' && x.trim()).map((x) => x.trim()).join(' ');
    if (words) return words;
  }
  if (body && body.error && typeof body.error === 'object' && typeof body.error.message === 'string' && body.error.message.trim()) {
    return body.error.message.trim();
  }
  if (e && Number.isInteger(e.status)) return refusalNoReason(e.status);
  return REFUSAL_NO_ANSWER;
}
function replySentence(reply) {
  const lines = reply && Array.isArray(reply.replies) ? reply.replies : [];
  return lines.filter((s) => typeof s === 'string' && s.trim()).map((s) => s.trim()).join(' ');
}
function linePrefix(lineNo) {
  return lineNo != null ? `Line ${lineNo}: ` : '';
}
let _focusSeq = 0;
async function answerInPlace({ lineNo = null, run, onReply, onRefused, mark, refresh, said, inputAt = null }) {
  const put = (patch) => { if (mark) mark(patch); };
  put({ pending: true, error: null });
  let reply;
  try {
    reply = await run();
  } catch (e) {
    const msg = refusalText(e);
    if (onRefused) onRefused((e && e.body) || null);
    if (refresh) { try { await refresh(); } catch (_) { /* the sheet says so itself */ } }
    // The refusal is shown on the line as role="alert", which speaks it — so it is NOT
    // also put in the live region (said twice otherwise; lab-order-card's precedent).
    // It is put AFTER the re-read, so it is recorded with the line's status as it now is.
    put({ pending: false, error: msg, focus: ++_focusSeq, line: lineNo, inputAt });
    return { ok: false, error: msg };
  }
  if (onReply) onReply(reply);
  const text = (said ? said(reply) : replySentence(reply)) || 'Answered.';
  const error = reply && reply.sendFailed ? SHEET_SEND_UNCONFIRMED : null;
  if (refresh) { try { await refresh(); } catch (_) { /* the sheet says so itself */ } }
  put({ pending: false, error, announce: `${linePrefix(lineNo)}${text}`, focus: ++_focusSeq, line: lineNo, inputAt });
  return { ok: true, reply };
}

// The sheet line a card belongs to (its outcome names the session).
function lineOfSession(turn, sessionId) {
  if (!turn || sessionId == null) return null;
  const lines = turn.sheet && Array.isArray(turn.sheet.lines) ? turn.sheet.lines : [];
  const hit = lines.find((l) => l && l.outcome && l.outcome.sessionId != null && String(l.outcome.sessionId) === String(sessionId));
  return hit ? hit.line : null;
}

// The status of the line a card belongs to, in this turn (null when no line names it).
function lineStatusOfSession(turn, sessionId) {
  const lines = turn && turn.sheet && Array.isArray(turn.sheet.lines) ? turn.sheet.lines : [];
  const hit = lines.find((l) => l && l.outcome && l.outcome.sessionId != null && String(l.outcome.sessionId) === String(sessionId));
  return hit ? hit.outcome.status || null : null;
}

// The sheet turn's per-card in-place state, merged by SESSION (key '*' = the sheet's own
// confirm-all): { pending, error, errorStatus } per card, and the last announcement +
// focus request on the turn. Every other turn is handed back as it was. An error is
// recorded with the status its line has AT THAT MOMENT (D1 b): the page applies this after
// the refusal's own re-read, so it is the re-read line's status; the line shows the error
// only while its status is still that one (lineErrorShown).
function withInPlace(turns, rosterId, key, patch) {
  const list = Array.isArray(turns) ? turns : [];
  const k = String(key);
  return list.map((t) => {
    if (!t || t.kind !== 'refillSheet' || t.rosterId !== rosterId) return t;
    const had = (t.inPlace && t.inPlace[k]) || {};
    const next = { ...t, inPlace: { ...(t.inPlace || {}), [k]: { ...had } } };
    if ('pending' in patch) next.inPlace[k].pending = !!patch.pending;
    if (patch.pending) next.inPlace[k].said = null;
    if ('error' in patch) {
      next.inPlace[k].error = patch.error || null;
      next.inPlace[k].errorStatus = patch.error && k !== '*' ? lineStatusOfSession(t, k) : null;
    }
    if (patch.announce) {
      next.inPlace[k].said = patch.announce;
      next.announce = { text: patch.announce, key: k };
    }
    if (patch.focus) {
      next.focusReq = { key: k, line: patch.line != null ? patch.line : null, seq: patch.focus, inputAt: patch.inputAt != null ? patch.inputAt : null };
    }
    return next;
  });
}

// Whether a line shows its card's error now (D1 b): whatever the line's status — a
// refusal whose re-read made the line drafted or not drafted is still said — but only
// while the line is still in the status it was said under, so an error does not linger
// once the line has moved on (a refused pick on a line Confirm all later files).
function lineErrorShown(answer, status) {
  if (!answer || !answer.error) return false;
  return answer.errorStatus == null || answer.errorStatus === status;
}

// ── I-FOCUS (#763 adjudication r1 D2) ─────────────────────────────────────────
// An in-place answer may move focus only if the provider has not tapped or typed
// anywhere on the page since the tap that started that answer — and even then only
// inside the answered line (or, for the sheet's Confirm all, to Confirm all). Whether the
// provider moved on comes from an explicit page-wide record of their input, captured when
// the answer starts, never from document.activeElement (a disabled or re-rendered control
// sends focus to <body>, which looks exactly like "nobody moved on").
let _inputCount = 0;
const _countedDocs = new WeakSet();
function installInputCounter(doc) {
  if (!doc || typeof doc.addEventListener !== 'function' || _countedDocs.has(doc)) return false;
  const bump = () => { _inputCount += 1; };
  doc.addEventListener('pointerdown', bump, true);
  doc.addEventListener('keydown', bump, true);
  _countedDocs.add(doc);
  return true;
}
function inputCount() {
  return _inputCount;
}
const movedOnSince = (inputAt, inputNow) => inputAt != null && inputNow != null && inputNow !== inputAt;

// After an answer, focus goes back to the line it was given on — never to the page's
// bottom, and never away from wherever the provider has moved on to in the meantime:
//   * any input on the page since the answer started (I-FOCUS) → left where it is;
//   * focus somewhere else on the page (the composer, another line) → left there;
//   * the clicked control is still on the line and usable → it keeps focus;
//   * else the line's next usable control — in its card first (what the answer moved
//     on to: the card to open and confirm), then its question, then anything on the
//     line — else the line itself (tabindex -1).
// `preventScroll` throughout: focus never moves the transcript. → what it did.
const FOCUSABLE_IN_LINE = 'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
function focusLineAfterAnswer(doc, { rosterId, line, clicked = null, inputAt = null, inputNow = null }) {
  if (!doc || rosterId == null || line == null) return null;
  if (movedOnSince(inputAt, inputNow)) return 'kept';
  const sheets = Array.from(doc.querySelectorAll('[data-roster-id]'));
  const sheet = sheets.find((el) => el.getAttribute('data-roster-id') === String(rosterId));
  const li = sheet ? Array.from(sheet.querySelectorAll('li[data-line]')).find((el) => el.getAttribute('data-line') === String(line)) : null;
  if (!li) return null;
  const active = doc.activeElement;
  const nowhere = !active || active === doc.body || active === doc.documentElement;
  if (!nowhere && !li.contains(active)) return 'kept';
  if (!nowhere && li.contains(active) && active !== li && !active.disabled) return 'kept';
  if (clicked && clicked.isConnected && li.contains(clicked) && !clicked.disabled) {
    clicked.focus({ preventScroll: true });
    return 'clicked';
  }
  let next = null;
  for (const region of ['.rs-line-card', '.rs-question', '.rs-line-body']) {
    const el = li.querySelector(region);
    next = el && el.querySelector(FOCUSABLE_IN_LINE);
    if (next) break;
  }
  (next || li).focus({ preventScroll: true });
  return next ? 'next' : 'line';
}

// …and after the sheet's Confirm all: the same gate, then focus to Confirm all if it is
// still usable, else the sheet itself — and only when focus is nowhere or still on the
// Confirm-all control (never from another line of the sheet the provider moved to).
function focusAfterConfirmAll(doc, { section = null, button = null, inputAt = null, inputNow = null }) {
  if (!doc) return null;
  if (movedOnSince(inputAt, inputNow)) return 'kept';
  const active = doc.activeElement;
  const nowhere = !active || active === doc.body || active === doc.documentElement;
  if (!nowhere && active !== button) return 'kept';
  const target = button && button.isConnected !== false && !button.disabled ? button : section;
  if (!target) return null;
  target.focus({ preventScroll: true });
  return target === button ? 'button' : 'sheet';
}

// The counts the summary heads with (the route's own `summary`, else counted here).
function summaryCounts(job) {
  if (job && job.summary) return job.summary;
  const counts = { drafted: 0, needsAttention: 0, notDrafted: 0, pending: 0 };
  for (const l of (job && Array.isArray(job.lines) ? job.lines : [])) {
    const s = l.outcome && l.outcome.status;
    if (s === 'drafted') counts.drafted += 1;
    else if (s === 'needs_attention') counts.needsAttention += 1;
    else if (s === 'not_drafted') counts.notDrafted += 1;
    else counts.pending += 1;
  }
  return counts;
}

// ── The printable per-line summary (F7) ──────────────────────────────────────

// One row per line of THIS sheet, in sheet order — nothing about any other resident. The
// resident is the chart the line was matched to (name + DOB) or, unmatched, the name as
// the sheet wrote it; the med is the chart med as matched, else the sheet's words.
function printRows(job) {
  const lines = job && Array.isArray(job.lines) ? job.lines : [];
  return lines.map((l) => {
    const o = l.outcome || { status: 'pending' };
    if (o.status === 'drafted') {
      // What was FILED (D1): the drafted resident and med, never the sheet's.
      const f = draftedFacts(o);
      let outcome = statusLabel('drafted');
      const facts = draftedStrengthFacts(o, l);
      // OWNER-1: a strength the chart changed is written on the paper too.
      const rule = facts.strengthRule === 'closest_on_chart' ? strengthRuleText(facts, l) : null;
      if (rule) outcome = `${outcome} — ${rule}`;
      if (f.threadFallback) outcome = `${outcome} — as a new prescription`;
      return {
        line: l.line,
        resident: residentLabel(f.patient) || DRAFTED_UNKNOWN.printResident,
        med: f.medName || DRAFTED_UNKNOWN.printMed,
        outcome,
      };
    }
    let outcome = statusLabel(o.status);
    if (o.status === 'not_drafted') outcome = `Not drafted — ${reasonText('not_drafted', o.reason)}`;
    const rule = o.strengthRule === 'closest_on_chart' ? strengthRuleText(o, l) : null;
    if (rule) outcome = `${outcome} — ${rule}`;
    return {
      line: l.line,
      resident: residentLabel(o.patient) || l.residentName || '(name not readable)',
      med: medAsMatched(o, l) || '(not readable)',
      outcome,
    };
  });
}

function printTitle(job) {
  const home = (job && job.facilityName) || 'Group home';
  return `${home} — refill sheet`;
}

// ── The sheet's cards stay on the sheet ──────────────────────────────────────

// The fields a reply-drawn turn carries about a batch or a card.
const CARD_FIELDS = ['batchId', 'batch', 'sessions', 'views', 'view', 'orders', 'orderIds', 'resolution', 'sessionId', 'sentOrders', 'opened', 'expanded', 'outcomes'];

// A reply about one of a sheet's cards (a pick, a confirm, a cancel) carries the WHOLE
// sheet batch, and the page's ordinary turn builder would draw every needs-attention card
// again under it — and again after the next answer. The sheet turn is where they live
// (reconciled by the same reply, R-S5-47), so any OTHER turn about a sheet's batch keeps
// only its words, marked `folded` (one with no words left is not drawn at all — the page
// keeps it in place so every later turn keeps its index). A turn about anything else is
// handed back as it was.
function foldSheetBatchTurns(turns) {
  const list = Array.isArray(turns) ? turns : [];
  const sheetBatches = new Set(list
    .filter((t) => t && t.kind === 'refillSheet' && t.batchId != null)
    .map((t) => String(t.batchId)));
  if (!sheetBatches.size) return list;
  let changed = false;
  const out = list.map((t) => {
    if (!t || t.kind === 'refillSheet' || t.batchId == null || !sheetBatches.has(String(t.batchId))) return t;
    changed = true;
    const kept = { ...t, folded: true };
    for (const k of CARD_FIELDS) delete kept[k];
    return kept;
  });
  return changed ? out : list;
}

// The job, without the batch half (that lives on the turn's batch fields).
function sheetOf(job) {
  const { batch: _batch, ...rest } = job || {};
  return rest;
}

// A new sheet turn, before the first poll answers.
function newSheetTurn(rosterId, extra = {}) {
  return { from: 'bot', kind: 'refillSheet', text: '', time: Date.now(), rosterId, sheet: { rosterId, status: 'queued', lines: [] }, ...extra };
}

// Fold a fresh GET into the sheet turn. The needs-attention cards arrive in /send's own
// batch shape, so they go through the page's own batch bookkeeping: `markBatchResults`
// (lib/order-batch) merges by SESSION and ADOPTS a card the turn does not hold yet (a scope
// (b) pick stages one), drops a cancelled one, and marks what filed. The review record
// (`opened`) and the expansion ride along untouched.
function withSheetJob(turn, job) {
  const base = { ...(turn || {}), kind: 'refillSheet', rosterId: (job && job.rosterId) || (turn && turn.rosterId), sheet: sheetOf(job) };
  const b = job && job.batch;
  if (!b || b.batchId == null || !Array.isArray(b.resolutions)) return base;
  const seeded = base.batchId != null && String(base.batchId) === String(b.batchId)
    ? base
    : { ...base, batchId: b.batchId, batch: [], sessions: [] };
  const merged = markBatchResults(seeded, b);
  const first = (Array.isArray(merged.batch) && merged.batch[0]) || null;
  return {
    ...merged,
    views: Array.isArray(b.views) ? b.views : (merged.views || []),
    sessionId: (first && first.sessionId) || (Array.isArray(merged.sessions) && merged.sessions[0] && merged.sessions[0].sessionId) || null,
  };
}

// The sheet turn's card group for ONE session, as a one-card turn the page's own Bubble
// renders: the same entry, roster row and view, so every control (open, pick, edit,
// confirm, cancel) is the existing one. `entryIndex` is where that entry sits in the
// sheet turn's batch — the page binds its handlers through it.
function cardTurnFor(turn, sessionId) {
  if (!turn || !Array.isArray(turn.batch) || sessionId == null) return null;
  const sid = String(sessionId);
  const entryIndex = turn.batch.findIndex((e) => e && String(e.sessionId) === sid);
  const session = (Array.isArray(turn.sessions) ? turn.sessions : []).find((s) => s && String(s.sessionId) === sid) || null;
  if (entryIndex < 0 && !session) return null;
  return {
    entryIndex: entryIndex < 0 ? null : entryIndex,
    turn: {
      from: 'bot',
      text: '',
      time: turn.time,
      batchId: turn.batchId,
      batch: entryIndex < 0 ? [] : [turn.batch[entryIndex]],
      sessions: session ? [session] : [],
      views: (Array.isArray(turn.views) ? turn.views : []).filter((v) => v && String(v.sessionId) === sid),
      sessionId: sid,
      sentOrders: turn.sentOrders,
      opened: turn.opened,
      expanded: turn.expanded,
      outcomes: turn.outcomes,
    },
  };
}

// ── The scope (b) search, as a small state machine ──────────────────────────
//   idle → searching → results | error;  results → picking → picked | error
function searchReducer(state, action) {
  const s = state || { phase: 'idle', candidates: [], error: null, pickedId: null };
  switch (action && action.type) {
    case 'search': return { ...s, phase: 'searching', error: null };
    case 'results': return { ...s, phase: 'results', candidates: Array.isArray(action.candidates) ? action.candidates : [], error: null };
    case 'pick': return { ...s, phase: 'picking', pickedId: action.patientId, error: null };
    case 'picked': return { ...s, phase: 'picked', error: null };
    case 'error': return { ...s, phase: s.phase === 'picking' ? 'results' : 'idle', error: action.error || 'Something went wrong.' };
    case 'reset': return { phase: 'idle', candidates: [], error: null, pickedId: null };
    default: return s;
  }
}

// The search and the pick, through an injected client (lib/api's ccmApi in the page); each answers what the reducer is
// told next. Never throws.
async function runLineSearch(client, rosterId, line) {
  try {
    const r = await client.searchRosterLine(rosterId, line);
    return { type: 'results', candidates: (r && r.candidates) || [] };
  } catch (e) {
    return { type: 'error', error: (e && e.message) || "The search couldn't be run." };
  }
}
async function runLinePick(client, rosterId, line, patientId) {
  try {
    const r = await client.pickRosterLinePatient(rosterId, line, patientId);
    return { type: 'picked', sessionId: r && r.sessionId };
  } catch (e) {
    return { type: 'error', error: (e && e.message) || "That patient couldn't be set." };
  }
}

// The scope (b) results as buttons — pure, so a test can press one (#749 r1 MINOR-5:
// the pick sends the CLICKED candidate's id, never another hit's). The accessible name
// carries the same tag status the eye sees beside it (MINOR-2): a hit the home's tags do
// not cover is the wrong-patient warning scope (b) exists to give.
function candidateActions(candidates, pick) {
  return (Array.isArray(candidates) ? candidates : []).map((c) => {
    const name = `${c.firstName || ''} ${c.lastName || ''}`.trim();
    const tag = c.inFacility ? 'tagged for this home' : 'not tagged for this home';
    return {
      id: c.id,
      name,
      dob: c.dob ? formatDob(c.dob) : null,
      tag,
      inFacility: !!c.inFacility,
      ariaLabel: `Use the chart of ${name}${c.dob ? `, born ${formatDob(c.dob)}` : ''} — ${tag}`,
      run: () => pick(c.id),
    };
  });
}

// The sheet's cards still waiting on the provider: its card SESSIONS not completed,
// cancelled or expired (#749 r1 MINOR-4) — never its needs-attention lines, some of which
// have no card at all (a resident the tags did not find).
const CLOSED_CARD_STATES = new Set(['completed', 'cancelled', 'expired']);
function openCardCount(turn) {
  return (turn && Array.isArray(turn.sessions) ? turn.sessions : [])
    .filter((x) => x && x.sessionId != null && !CLOSED_CARD_STATES.has(x.state)).length;
}

// "Try again" / "Draft anyway" (#747 r2 D4), as a small state machine:
//   idle → working → done | error (an error can be tried again).
function lineActionReducer(state, action) {
  const s = state || { phase: 'idle', error: null };
  switch (action && action.type) {
    case 'start': return { phase: 'working', error: null };
    case 'done': return { phase: 'done', error: null };
    case 'error': return { phase: 'idle', error: action.error || 'Something went wrong.' };
    default: return s;
  }
}
async function runLineAction(client, kind, rosterId, line) {
  try {
    const r = kind === 'retry'
      ? await client.retryRosterLine(rosterId, line)
      : await client.draftRosterLineAnyway(rosterId, line);
    return { type: 'done', result: r || null };
  } catch (e) {
    return { type: 'error', error: (e && e.message) || (kind === 'retry' ? "The line couldn't be retried." : "The card couldn't be staged.") };
  }
}

// The sheet's Discard button: the flow below, told how many CARDS are open (MINOR-4).
function discardSheet({ client, confirm, turn }) {
  return discardSheetFlow({ client, confirm, rosterId: turn && turn.rosterId, openCards: openCardCount(turn) });
}

// "Discard sheet", behind the app's own confirm dialog (never a native alert).
// → { discarded: true, cardsEnded } | { discarded: false, cancelled: true } | { discarded: false, error }
async function discardSheetFlow({ client, confirm, rosterId, openCards = 0 }) {
  const ok = await confirm({
    title: 'Discard this sheet?',
    body: openCards > 0
      ? `The ${openCards} card${openCards === 1 ? '' : 's'} still waiting on you will be cancelled. Drafts already in Elation stay there, unsigned.`
      : 'No cards are waiting on you. Drafts already in Elation stay there, unsigned.',
    confirmLabel: 'Discard sheet',
    cancelLabel: 'Keep it',
    danger: true,
  });
  if (!ok) return { discarded: false, cancelled: true };
  try {
    const r = await client.discardRosterSheet(rosterId);
    return { discarded: true, cardsEnded: (r && r.cardsEnded) || 0 };
  } catch (e) {
    return { discarded: false, error: (e && e.message) || "The sheet couldn't be discarded." };
  }
}

module.exports = {
  ROSTER_ACCEPT, selectionProblem, chartsInScopeText,
  PROGRESS_STEPS, progressStepOf, POLL_DELAYS_MS, POLL_CAP_MS, POLL_MAX_MS, POLL_MAX_ERRORS,
  nextPollDelay, pollDecision, createSheetPoller,
  MODEL_NAMES, modelName, readByText,
  ERROR_TEXT, errorText, NEEDS_ATTENTION_TEXT, NOT_DRAFTED_TEXT, reasonText, STATUS_LABELS, statusLabel,
  duplicateOfText, ACTION_LABELS, THREAD_FALLBACK_TEXT, strengthsOf, medAsMatched,
  draftedFacts, draftedStrengthFacts, DRAFTED_UNKNOWN,
  lineActionReducer, runLineAction,
  LEGIBILITY_LABELS, legibilityLabel, formatDob, residentLabel, sheetMedText, strengthRuleText,
  elationChartUrl, sheetPageUrl, summaryCounts, printRows, printTitle,
  foldSheetBatchTurns, sheetOf, newSheetTurn, withSheetJob, cardTurnFor,
  searchReducer, runLineSearch, runLinePick, discardSheetFlow,
  uploadReadiness, candidateActions, openCardCount, submitUpload, UPLOAD_NO_ID, performUpload, discardSheet,
  ROW_BAND_SCALE, CROP_MIN_ROW, CROP_MAX_ROW, CROP_PAD, CROP_MIN_BAND, CROP_MAX_BAND, CROP_DEFAULT_PAGE,
  lineRowBand, cropBand, cropLayout, lineCrop, centredScrollTop,
  SHEET_SEND_UNCONFIRMED, REFUSAL_NO_ANSWER, refusalNoReason, refusalText, replySentence, answerInPlace, lineOfSession,
  lineStatusOfSession, withInPlace, lineErrorShown, installInputCounter, inputCount, focusLineAfterAnswer, focusAfterConfirmAll,
};
